/**
 * Free shipping nudge — booster "Add" button controller.
 *
 * Handles clicks on [data-fs-add] buttons rendered by snippets/free-shipping-bar.liquid
 * (inside the cart drawer / cart page). Adds the booster variant to the cart using the
 * standard CartLinesUpdateEvent flow (same pattern as upsell-popup.js) so the cart
 * drawer re-renders itself — the bar then flips to its "FREE shipping unlocked" state.
 *
 * TRACKING (visible in Shopify admin on the order):
 *   • The add carries a hidden line-item property `_Added via: Free shipping bar`.
 *   • It also sets the cart attribute `lucy_freeshipping_upsell: yes` (naming
 *     matches the existing `lucy_upsell_clicked` convention), which lands in the
 *     order's custom attributes — the Shopify Flow workflow "Tag orders with
 *     free shipping upsell" tags those orders `lucy-freeshipping-upsell`.
 *
 * SCRUB FREE SHIPPING (AU, since 2026-09-17): after every cart change we check
 * whether the cart has the scrub + another product and totals data-scrub-min,
 * and add or remove the data-scrub-code discount to match (other codes are
 * kept). Cart attribute `lucy_scrub_fs: applied` marks carts where it applied;
 * adds from the scrub booster set `lucy_scrub_fs_offer: took_bar`. Config lives
 * on <upsell-popup> in snippets/upsell-popup.liquid.
 *
 * UK BAR (2026-10-15): GBP bars render hidden and are filled in here with the
 * A$ threshold converted at Shopify.currency.rate — see renderConvertedBars().
 *
 * Uses event delegation on document so it survives Section Rendering API morphs,
 * and a window guard so it only initialises once no matter how many times the
 * script tag is re-injected. Loaded from layout/theme.liquid.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  if (window.__fsNudgeInit) return;
  window.__fsNudgeInit = true;

  const routes = (window.Theme && window.Theme.routes) || {};

  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((c) => {
      if (c.dataset.sectionId) ids.push(c.dataset.sectionId);
    });
    return ids;
  }

  async function getCart() {
    const res = await fetch((routes.cart_url || '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return res.json();
  }

  async function setCartAttributes(attrs) {
    try {
      await fetch(routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: attrs }),
      });
    } catch (e) { /* tracking must never break the flow */ }
  }

  function publishEvent(name, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* no-op */ }
  }

  async function addVariant(variantId, sourceLabel) {
    const items = [{ id: Number(variantId), quantity: 1, properties: { '_Added via': sourceLabel || 'Free shipping bar' } }];
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
        detail: { source: 'free-shipping-nudge' },
      })
    );
    try {
      const res = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: cartSectionIds().join(',') }),
      });
      const data = await res.json();
      const cart = await getCart();
      if (data && data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'free-shipping-nudge' },
        });
        return null;
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'free-shipping-nudge', sections: data.sections, didError: false },
      });
      return cart;
    } catch (err) {
      deferred.reject(err);
      return null;
    }
  }

  document.addEventListener('click', async (event) => {
    const btn = event.target instanceof Element ? event.target.closest('[data-fs-add]') : null;
    if (!btn || btn.disabled) return;
    const variantId = btn.dataset.fsVariantId;
    if (!variantId) return;

    const isScrubOffer = btn.hasAttribute('data-fs-scrub-offer');
    const orig = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    const cart = await addVariant(variantId, isScrubOffer ? 'Free shipping bar (scrub)' : 'Free shipping bar');
    if (cart) {
      setCartAttributes(isScrubOffer ? { lucy_scrub_fs_offer: 'took_bar' } : { 'lucy_freeshipping_upsell': 'yes' });
      publishEvent('free_shipping_bar_added', {
        variant_id: String(variantId),
        cart_total: cart.total_price,
      });
    } else {
      btn.disabled = false;
      btn.textContent = orig;
    }
    // On success the cart sections morph and the bar re-renders in its new state.
  });

  // ---- Scrub free shipping: keep the discount code in step with the cart ----
  const cfgEl = document.querySelector('upsell-popup');
  const scrub = {
    code: (cfgEl && cfgEl.dataset.scrubCode) || '',
    handle: (cfgEl && cfgEl.dataset.scrubHandle) || '',
    min: parseInt(cfgEl && cfgEl.dataset.scrubMin, 10) || 0,
  };
  let reconciling = false;
  let reconcileAgain = false;

  async function reconcileScrubShipping() {
    if (!scrub.code || !scrub.handle) return;
    if (reconciling) { reconcileAgain = true; return; }
    reconciling = true;
    try {
      const cart = await getCart();
      const inAu = !!(window.Shopify && window.Shopify.country === 'AU');
      let scrubQty = 0;
      let otherQty = 0;
      (cart.items || []).forEach((i) => { if (i.handle === scrub.handle) scrubQty += i.quantity; else otherQty += i.quantity; });
      const eligible = inAu && scrubQty > 0 && otherQty > 0 && (cart.total_price || 0) >= scrub.min;
      const codes = (cart.discount_codes || []).map((d) => d.code);
      const isOurs = (c) => String(c).toUpperCase() === scrub.code.toUpperCase();
      const hasCode = codes.some(isOurs);
      if (eligible === hasCode) return;
      const next = eligible ? codes.concat(scrub.code) : codes.filter((c) => !isOurs(c));
      await fetch(routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: next.join(','), attributes: eligible ? { lucy_scrub_fs: 'applied' } : { lucy_scrub_fs: '', lucy_scrub_fs_offer: '' } }),
      });
    } catch (e) {
      /* never block the cart */
    } finally {
      reconciling = false;
      if (reconcileAgain) { reconcileAgain = false; reconcileScrubShipping(); }
    }
  }

  document.addEventListener('shopify:cart:lines-update', async (event) => {
    try {
      if (event.promise) await event.promise;
      else await new Promise((r) => setTimeout(r, 800));
    } catch (e) { /* failed updates still get re-checked */ }
    reconcileScrubShipping();
  });
  reconcileScrubShipping();

  // ---- UK (GBP) bar: convert the A$ threshold in the browser ----------------
  // FREESHIPPING100 is an A$100 automatic discount; Shopify converts that
  // minimum at the market rate, which Liquid can't see. GBP bars arrive hidden
  // with data-fs-convert + data-fs-pending (snippets/free-shipping-bar.liquid),
  // and we fill them in here. Rounded UP to the whole pound so the bar never
  // claims "unlocked" before checkout agrees. Re-runs whenever a section morph
  // puts data-fs-pending back. Keep in sync with fsThresholdFor() in
  // assets/upsell-popup.js.
  function convertedThreshold(shopCents) {
    const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
    if (!shopCents || !rate) return 0;
    return Math.ceil((shopCents * rate) / 100) * 100;
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function renderConvertedBars() {
    document.querySelectorAll('.fs-bar[data-fs-convert][data-fs-pending]').forEach((bar) => {
      const threshold = convertedThreshold(parseInt(bar.dataset.fsThresholdShop, 10));
      const msg = bar.querySelector('[data-fs-msg]');
      if (!threshold || !msg) return; // no rate → leave it hidden rather than show a wrong number
      const total = parseInt(bar.dataset.fsTotal, 10) || 0;
      const remaining = threshold - total;
      const pct = Math.min(100, Math.floor((total * 100) / threshold));

      bar.classList.toggle('fs-bar--unlocked', remaining <= 0);
      msg.innerHTML = remaining > 0
        ? 'You\'re only <strong>' + formatMoney(remaining, bar.dataset.fsCurrency) + '</strong> away from <strong>FREE shipping</strong>! 🚚'
        : '🎉 Congrats — you\'ve unlocked <strong>FREE shipping</strong>!';
      const fill = bar.querySelector('[data-fs-fill]');
      if (fill) fill.style.width = pct + '%';
      const track = bar.querySelector('.fs-bar__track');
      if (track) track.setAttribute('aria-valuenow', String(pct));

      // Same pick as the Liquid path: first candidate (priority order) that
      // covers the gap, else the first one.
      const candidates = Array.prototype.slice.call(bar.querySelectorAll('[data-fs-candidate]'));
      candidates.forEach((c) => { c.hidden = true; });
      if (remaining > 0 && candidates.length) {
        const covering = candidates.find((c) => (parseInt(c.dataset.fsPrice, 10) || 0) >= remaining);
        const pick = covering || candidates[0];
        const btn = pick.querySelector('[data-fs-add]');
        if (btn && !btn.disabled) btn.textContent = covering ? 'Add & unlock' : 'Add';
        pick.hidden = false;
      }
      bar.removeAttribute('data-fs-pending');
    });
  }

  new MutationObserver(renderConvertedBars).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-fs-pending', 'data-fs-total'],
  });
  renderConvertedBars();
})();
